// Dashboard recommendation utility based on user role and permissions

/**
 * Determines the recommended dashboard for a user based on their role
 * @param {Object} user - User object with role information
 * @returns {Object} - Recommended dashboard details
 */
export const getRecommendedDashboard = (user) => {
  if (!user) {
    return {
      path: '/dashboard',
      name: 'Overview',
      reason: 'Default dashboard'
    };
  }

  // Check user's role name or permissions
  const roleName = user.role?.name?.toLowerCase() || '';
  const permissions = user.role?.permissions || [];

  // Executive/Management roles
  if (roleName.includes('executive') ||
      roleName.includes('ceo') ||
      roleName.includes('cfo') ||
      roleName.includes('owner') ||
      roleName.includes('director')) {
    return {
      path: '/dashboards/executive',
      name: 'Executive Dashboard',
      reason: 'Best for strategic oversight and business performance'
    };
  }

  // Operations/Warehouse roles
  if (roleName.includes('operations') ||
      roleName.includes('warehouse') ||
      roleName.includes('fulfillment') ||
      roleName.includes('inventory')) {
    return {
      path: '/dashboards/operations',
      name: 'Operations Dashboard',
      reason: 'Optimized for inventory and fulfillment management'
    };
  }

  // Purchasing/Procurement roles
  if (roleName.includes('purchasing') ||
      roleName.includes('procurement') ||
      roleName.includes('buyer')) {
    return {
      path: '/dashboards/purchasing',
      name: 'Purchasing Dashboard',
      reason: 'Focused on supplier management and procurement'
    };
  }

  // Amazon/FBA roles
  if (roleName.includes('amazon') ||
      roleName.includes('fba') ||
      roleName.includes('ecommerce')) {
    return {
      path: '/dashboards/amazon',
      name: 'Amazon Manager Dashboard',
      reason: 'Specialized for Amazon FBA operations'
    };
  }

  // Permission-based fallback
  if (permissions.includes('analytics:read')) {
    // Check what they can access
    const hasInventoryAccess = permissions.some(p => p.startsWith('inventory:'));
    const hasPOAccess = permissions.some(p => p.startsWith('purchase-orders:'));
    const hasOrderAccess = permissions.some(p => p.startsWith('orders:'));

    if (hasInventoryAccess && hasOrderAccess) {
      return {
        path: '/dashboards/operations',
        name: 'Operations Dashboard',
        reason: 'Based on your inventory and order management permissions'
      };
    }

    if (hasPOAccess) {
      return {
        path: '/dashboards/purchasing',
        name: 'Purchasing Dashboard',
        reason: 'Based on your purchasing permissions'
      };
    }
  }

  // Default to main dashboard
  return {
    path: '/dashboard',
    name: 'Overview Dashboard',
    reason: 'Comprehensive view of all operations'
  };
};

/**
 * Gets all accessible dashboards for a user
 * @param {Object} user - User object
 * @returns {Array} - List of accessible dashboard objects
 */
export const getAccessibleDashboards = (user) => {
  const allDashboards = [
    {
      id: 'overview',
      path: '/dashboard',
      name: 'Overview',
      icon: 'LayoutDashboard',
      description: 'Comprehensive business metrics',
      requiredPermissions: []
    },
    {
      id: 'executive',
      path: '/dashboards/executive',
      name: 'Executive',
      icon: 'Briefcase',
      description: 'Revenue, profit, and growth',
      requiredPermissions: ['analytics:read']
    },
    {
      id: 'operations',
      path: '/dashboards/operations',
      name: 'Operations',
      icon: 'Package2',
      description: 'Inventory and fulfillment',
      requiredPermissions: ['analytics:read', 'inventory:read']
    },
    {
      id: 'purchasing',
      path: '/dashboards/purchasing',
      name: 'Purchasing',
      icon: 'ShoppingCart',
      description: 'POs and suppliers',
      requiredPermissions: ['analytics:read', 'purchase-orders:read']
    },
    {
      id: 'amazon',
      path: '/dashboards/amazon',
      name: 'Amazon Manager',
      icon: 'ShoppingBag',
      description: 'FBA metrics and compliance',
      requiredPermissions: ['analytics:read']
    }
  ];

  if (!user || !user.role) {
    return allDashboards; // Show all if no role info
  }

  const userPermissions = user.role.permissions || [];

  return allDashboards.filter(dashboard => {
    // If no permissions required, it's accessible
    if (dashboard.requiredPermissions.length === 0) {
      return true;
    }

    // Check if user has all required permissions
    return dashboard.requiredPermissions.every(required =>
      userPermissions.includes(required)
    );
  });
};

/**
 * Gets a personalized welcome message for the user
 * @param {Object} user - User object
 * @returns {string} - Welcome message
 */
export const getWelcomeMessage = (user) => {
  if (!user) {
    return 'Welcome to GABHAN ERP';
  }

  const hour = new Date().getHours();
  let greeting = 'Hello';

  if (hour < 12) {
    greeting = 'Good morning';
  } else if (hour < 18) {
    greeting = 'Good afternoon';
  } else {
    greeting = 'Good evening';
  }

  const firstName = user.name?.split(' ')[0] || user.name || 'there';
  const roleName = user.role?.name || '';

  if (roleName) {
    return `${greeting}, ${firstName}`;
  }

  return `${greeting}, ${firstName}`;
};

/**
 * Gets role-specific insights or tips
 * @param {Object} user - User object
 * @returns {Array} - Array of insight objects
 */
export const getRoleInsights = (user) => {
  if (!user || !user.role) {
    return [];
  }

  const roleName = user.role.name?.toLowerCase() || '';

  const insights = {
    executive: [
      { type: 'tip', message: 'Review your profit margins weekly to identify optimization opportunities' },
      { type: 'insight', message: 'Top 20% of products typically drive 80% of revenue' }
    ],
    operations: [
      { type: 'alert', message: 'Check critical alerts daily to prevent stockouts' },
      { type: 'tip', message: 'Monitor warehouse capacity to optimize inventory distribution' }
    ],
    purchasing: [
      { type: 'reminder', message: 'Review supplier performance monthly to maintain quality standards' },
      { type: 'tip', message: 'Act on reorder recommendations within 24 hours' }
    ],
    amazon: [
      { type: 'critical', message: 'Maintain IPI score above 500 to avoid storage limits' },
      { type: 'tip', message: 'Address stranded inventory weekly to maximize sales potential' }
    ]
  };

  // Find matching insights
  for (const [key, messages] of Object.entries(insights)) {
    if (roleName.includes(key)) {
      return messages;
    }
  }

  return [];
};

/**
 * Checks if user has permission to access a specific dashboard
 * @param {Object} user - User object
 * @param {string} dashboardPath - Dashboard path to check
 * @returns {boolean} - Whether user can access the dashboard
 */
export const canAccessDashboard = (user, dashboardPath) => {
  const accessibleDashboards = getAccessibleDashboards(user);
  return accessibleDashboards.some(d => d.path === dashboardPath);
};

/**
 * Gets dashboard analytics preferences for a role
 * @param {Object} user - User object
 * @returns {Object} - Recommended settings
 */
export const getDashboardPreferences = (user) => {
  if (!user || !user.role) {
    return {
      defaultPeriod: '30d',
      refreshInterval: 30000,
      showAlerts: true,
      compactView: false
    };
  }

  const roleName = user.role.name?.toLowerCase() || '';

  // Role-specific preferences
  if (roleName.includes('executive')) {
    return {
      defaultPeriod: '90d', // Executives like longer trends
      refreshInterval: 60000, // Less frequent updates
      showAlerts: false, // Focus on trends, not alerts
      compactView: false
    };
  }

  if (roleName.includes('operations')) {
    return {
      defaultPeriod: '7d', // Operations focus on recent data
      refreshInterval: 30000, // Frequent updates
      showAlerts: true, // Critical for operations
      compactView: false
    };
  }

  if (roleName.includes('purchasing')) {
    return {
      defaultPeriod: '30d',
      refreshInterval: 60000,
      showAlerts: true, // Important for delays
      compactView: false
    };
  }

  if (roleName.includes('amazon')) {
    return {
      defaultPeriod: '30d', // Amazon metrics are typically monthly
      refreshInterval: 30000,
      showAlerts: true, // Compliance is critical
      compactView: false
    };
  }

  // Default preferences
  return {
    defaultPeriod: '30d',
    refreshInterval: 30000,
    showAlerts: true,
    compactView: false
  };
};
